import { searchQueryToken } from './derived.js';
import { sortAllowsDateRange, type SortOptionId } from './sortOptions.js';

// THE SORT CONTROL AND THE SEARCH BOX — PURE VIEW RULES, shared by the cross-account page and the account tab (Run B).
//
// ⚠️⚠️ SEARCH IS ITS OWN NARROW PATH, AND THE PAGE MUST SAY SO. Firestore allows one `array-contains` per query and
// Category already uses it, so a search resolves with SCOPE ALONE. While the box has a usable query the other filters and
// the sort are DISABLED, visibly, with the sentence below — never silently ignored, and never narrowed in memory
// (that breaks pagination; the upgrade and why are written at refuseSearchCombination in server/.../assets.ts).
// The disabled filters keep their values, so clearing the search restores the page exactly as it was.

export type SearchState =
  | { active: false; query: null; hint: null }
  /** Something was typed but nothing in it is searchable (under two letters or numbers). Nothing is sent. */
  | { active: false; query: null; hint: string }
  | { active: true; query: string; hint: null };

export const SEARCH_MIN_HINT = 'Type at least two letters or numbers to search.';

export function searchState(raw: string): SearchState {
  const q = raw.trim();
  if (q === '') return { active: false, query: null, hint: null };
  if (searchQueryToken(q) === null) return { active: false, query: null, hint: SEARCH_MIN_HINT };
  return { active: true, query: q, hint: null };
}

/** Shown beside the disabled controls while a search is running. */
export const SEARCH_DISABLES_FILTERS_SENTENCE =
  'Searching looks across every asset in the chosen scope, newest first. Clear the search to use the other filters or to sort.';

/** Shown beside the disabled controls while "Not reviewed by Kyck" is on (Run C). Staff only. */
export const UNREVIEWED_DISABLES_FILTERS_SENTENCE =
  'Showing every asset in the chosen scope that nobody at Kyck has reviewed, newest first. Turn it off to use the other filters or to sort.';

/**
 * ⚠️ WHICH NARROW PATH THE PAGE IS ON. Search and "Not reviewed by Kyck" are both scope-alone queries (Firestore allows one
 * array-contains per query, and each already needs its own index shape), so each disables every other control. When both are
 * set SEARCH WINS and the toggle is disabled: the value stays in the form, so clearing the search restores it.
 */
export type NarrowMode = 'search' | 'unreviewed' | null;

export function narrowMode(form: { search: string; kyckUnreviewed?: boolean }): NarrowMode {
  if (searchState(form.search).active) return 'search';
  return form.kyckUnreviewed === true ? 'unreviewed' : null;
}

/** Shown under the date range while it is unavailable. */
export const DATE_NEEDS_DATE_SORT_SENTENCE = 'A date range only works when sorting by date (Newest first or Oldest first).';

export function dateRangeAvailable(sort: SortOptionId, searching: boolean): boolean {
  return !searching && sortAllowsDateRange(sort);
}

/**
 * ⚠️ Choosing Name or Largest CLEARS the date range: the range is on `createdAt` and Firestore wants a range filter's
 * field first in orderBy, so the combination cannot be queried. Clearing it (rather than hiding a still-set range that
 * would 400) keeps what the page shows true to what it is asking.
 */
export function chooseSort<T extends { sort: SortOptionId; from: string; to: string }>(form: T, sort: SortOptionId): T {
  return sortAllowsDateRange(sort) ? { ...form, sort } : { ...form, sort, from: '', to: '' };
}

/** `YYYY-MM-DD` (local calendar day) -> the ISO instant of its start, `addDays` later. A day not on the calendar is dropped. */
export function localDayStart(ymd: string, addDays = 0): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return undefined;
  const [y, mo, day] = [Number(m[1]), Number(m[2]) - 1, Number(m[3])];
  const base = new Date(y, mo, day);
  // ⚠️ Date rolls 2026-13-45 over into a real date; a day that is not on the calendar is dropped instead.
  if (base.getFullYear() !== y || base.getMonth() !== mo || base.getDate() !== day) return undefined;
  return new Date(y, mo, day + addDays).toISOString();
}
